import { CalendarPlus, Clock } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { CalendarActivity, CalendarResource } from '@/api/resourceCalendarApi'
import { STATE_TAGS, avatarTone, dayParts, formatDueMonth, formatHours, isOverdue } from '@/lib/myTasks'
import { agendaGroups } from '@/lib/myTasksViews'
import { cn } from '@/lib/utils'
import { TypeBadge } from './ActivityCard'
import type { PlanPreset } from './PlanTaskDialog'

/**
 * Everything in the window, in order, a day at a time: what, when, who and the
 * record behind it. The easiest view to read top to bottom — and the natural
 * one for a technician looking at their own week.
 */
export function AgendaView({
  days,
  today,
  activities,
  resources,
  canManage,
  onOpen,
  onAdd,
}: {
  days: string[]
  today: string
  activities: CalendarActivity[]
  resources: CalendarResource[]
  canManage: boolean
  onOpen: (activity: CalendarActivity) => void
  onAdd: (preset: PlanPreset) => void
}) {
  const people = new Map(resources.map((r) => [r.userId, r]))
  const groups = agendaGroups(activities, days)
  return (
    <div className='min-h-0 flex-1 overflow-y-auto rounded-2xl border bg-card shadow-sm'>
      {groups.map(({ date, items }) => {
        const p = dayParts(date)
        const isToday = date === today
        const hours = items.filter((a) => a.ownerId && a.type !== 'UA').reduce((s, a) => s + a.hours, 0)
        return (
          <section key={date} aria-label={`${p.weekday} ${p.day} ${p.month}`} className='border-b last:border-b-0'>
            <header
              className={cn(
                'sticky top-0 z-10 flex items-center gap-3 border-b bg-card/95 px-5 py-2.5 backdrop-blur',
                isToday && 'bg-primary/[0.06]',
              )}
            >
              <span
                className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-full text-base font-semibold tabular-nums',
                  isToday ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
                )}
              >
                {p.day}
              </span>
              <div className='leading-tight'>
                <p className={cn('text-sm font-medium', isToday && 'text-primary')}>
                  {isToday ? 'Today' : p.weekday} · {p.month}
                </p>
                <p className='text-xs text-muted-foreground'>
                  {items.length === 0 ? 'Nothing planned' : `${items.length} ${items.length === 1 ? 'task' : 'tasks'} · ${formatHours(hours)} h`}
                </p>
              </div>
              {canManage && (
                <button
                  type='button'
                  onClick={() => onAdd({ date })}
                  className='ml-auto flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-primary/10 hover:text-primary'
                >
                  <CalendarPlus className='h-3.5 w-3.5' /> Plan a task
                </button>
              )}
            </header>
            {items.length > 0 && (
              <ul className='divide-y'>
                {items.map((a) => {
                  const owner = a.ownerId ? people.get(a.ownerId) : null
                  const late = isOverdue(a, today)
                  const tag = STATE_TAGS[a.state]
                  return (
                    <li key={a.key}>
                      <div
                        role='button'
                        tabIndex={0}
                        onClick={() => onOpen(a)}
                        onKeyDown={(e) => {
                          if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault()
                            onOpen(a)
                          }
                        }}
                        className='grid cursor-pointer grid-cols-[4rem_1fr_auto] items-center gap-4 px-5 py-2.5 transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none'
                      >
                        <span className='text-xs tabular-nums text-muted-foreground'>{a.startTime ?? 'Any time'}</span>
                        <span className='min-w-0'>
                          <span className='flex items-center gap-2'>
                            <TypeBadge code={a.type} />
                            {tag && <span className='rounded bg-primary/15 px-1 text-[10px] font-semibold text-primary'>{tag}</span>}
                            <span className='truncate text-sm font-medium'>{a.description}</span>
                            {late && <Clock className='h-3.5 w-3.5 shrink-0 text-destructive' aria-label='Overdue' />}
                          </span>
                          <span className='mt-0.5 flex items-center gap-3 text-xs text-muted-foreground'>
                            {a.sourceRef &&
                              (a.href ? (
                                <Link
                                  to={a.href}
                                  onClick={(e) => e.stopPropagation()}
                                  className='font-medium text-primary underline-offset-2 hover:underline'
                                >
                                  {a.sourceRef}
                                </Link>
                              ) : (
                                <span>{a.sourceRef}</span>
                              ))}
                            <span className={cn(late && 'font-medium text-destructive')}>D {formatDueMonth(a.dueDate)}</span>
                            <span className='tabular-nums'>{formatHours(a.hours)} h</span>
                          </span>
                        </span>
                        <span className='flex items-center gap-2 text-xs'>
                          {owner ? (
                            <>
                              <span
                                className={cn(
                                  'flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold',
                                  avatarTone(owner.name),
                                )}
                              >
                                {owner.initials}
                              </span>
                              <span className='hidden max-w-[10rem] truncate sm:inline'>{owner.name}</span>
                            </>
                          ) : (
                            <span className='rounded-full border border-dashed px-2 py-0.5 text-muted-foreground'>Unassigned</span>
                          )}
                        </span>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </section>
        )
      })}
    </div>
  )
}
